import type { ComponentPropsWithRef, ReactNode } from "react";
import { ArrowUp, Columns3 } from "lucide-react";
import { cx } from "./cx";
import { MenuCheckboxItem, MenuContent, MenuItem, MenuLabel, MenuRoot, MenuSeparator, MenuTrigger } from "./Menu";

export function GridWrap({ children, className, ...props }: ComponentPropsWithRef<"div">) {
  return (
    <div
      className={cx(
        "mx-4 min-h-0 flex-1 overflow-auto rounded-t-xl border border-b-0 border-divider bg-bg max-compact:mx-2",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function Grid({
  children,
  className,
  minWidth,
  ...props
}: ComponentPropsWithRef<"table"> & { minWidth: number }) {
  return (
    <table
      className={cx("table-fixed border-separate border-spacing-0", className)}
      style={{ width: `max(100%, ${String(minWidth)}px)` }}
      {...props}
    >
      {children}
    </table>
  );
}

export type SortDirection = "ascending" | "descending" | "none";

export function GridHeadCell({
  children,
  sticky = false,
  sort,
  onSort,
  width,
  label,
  left,
  resize,
}: {
  children?: ReactNode;
  left?: number | undefined;
  resize?: ReactNode;
  sticky?: boolean;
  sort?: SortDirection | undefined;
  onSort?: (() => void) | undefined;
  width?: number | undefined;
  label?: string | undefined;
}) {
  return (
    <th
      scope="col"
      aria-sort={onSort ? (sort ?? "none") : undefined}
      style={{ ...(width === undefined ? {} : { width }), ...(left === undefined ? {} : { left }) }}
      className={cx(
        "sticky top-0 z-head h-[38px] border-r border-b border-r-divider border-b-border bg-surface p-0 text-left align-middle text-sm font-medium text-muted last:border-r-0",
        sticky && "left-0 z-pinned-head shadow-[inset_-1px_0_0_var(--color-divider)]",
        sort && sort !== "none" && "text-fg",
      )}
    >
      {onSort ? (
        <button
          type="button"
          onClick={onSort}
          className="group flex size-full items-center gap-1 overflow-hidden px-2.5 text-left leading-tight hover:bg-surface-2 hover:text-fg"
        >
          <span className="truncate">{children}</span>
          <ArrowUp
            size={10}
            strokeWidth={2}
            aria-hidden="true"
            className={cx(
              "flex-none",
              sort === "ascending" || sort === "descending"
                ? "text-link opacity-100"
                : "opacity-0 group-hover:opacity-50",
              sort === "descending" && "rotate-180",
            )}
          />
        </button>
      ) : (
        <span className="flex h-full items-center px-2.5 leading-tight">
          {children ?? (label ? <span className="sr-only">{label}</span> : null)}
        </span>
      )}
      {resize}
    </th>
  );
}

export function GridRow({
  children,
  className,
  muted = false,
  selected = false,
  ...props
}: ComponentPropsWithRef<"tr"> & { muted?: boolean; selected?: boolean }) {
  return (
    <tr
      data-selected={selected || undefined}
      className={cx(
        "group/row [&>td]:bg-bg hover:[&>td]:bg-row-hover data-[selected]:[&>td]:bg-row-selected",
        muted && "[&>td]:text-muted",
        className,
      )}
      {...props}
    >
      {children}
    </tr>
  );
}

export function GridCell({
  children,
  className,
  sticky = false,
  ...props
}: ComponentPropsWithRef<"td"> & { sticky?: boolean }) {
  return (
    <td
      className={cx(
        "h-10 overflow-hidden border-b border-divider px-2.5 align-middle text-ellipsis whitespace-nowrap text-muted",
        sticky &&
          "sticky left-0 z-pinned-cell shadow-[inset_-1px_0_0_var(--color-divider)] group-data-[selected]/row:shadow-[inset_3px_0_0_var(--color-link),inset_-1px_0_0_var(--color-divider)]",
        className,
      )}
      {...props}
    >
      {children}
    </td>
  );
}

export function GridEmptyRow({ columns, children }: { columns: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={columns} className="h-[120px] border-b border-divider bg-bg px-4 text-center text-muted">
        {children}
      </td>
    </tr>
  );
}

export interface ColumnToggle {
  id: string;
  label: string;
  visible: boolean;
  canHide: boolean;
  onToggle: (visible: boolean) => void;
}

export function ColumnsMenu({ columns, onReset }: { columns: readonly ColumnToggle[]; onReset?: () => void }) {
  return (
    <MenuRoot>
      <MenuTrigger className="inline-flex h-7 min-w-7 items-center justify-center gap-1.5 rounded-lg border border-border bg-bg px-2 text-md text-muted hover:bg-surface-2 hover:text-fg aria-expanded:bg-surface-3 aria-expanded:text-fg">
        <Columns3 size={14} strokeWidth={1.8} aria-hidden="true" />
        <span className="max-compact:sr-only">Columns</span>
      </MenuTrigger>
      <MenuContent aria-label="Show or hide columns">
        <MenuLabel>Columns</MenuLabel>
        {columns.map((c) => (
          <MenuCheckboxItem
            key={c.id}
            checked={c.visible}
            disabled={!c.canHide}
            onSelect={(e) => {
              e.preventDefault();
            }}
            onCheckedChange={(checked) => {
              c.onToggle(checked);
            }}
          >
            {c.label}
            {c.canHide ? null : <span className="ml-2 text-xs text-muted">Always</span>}
          </MenuCheckboxItem>
        ))}
        {onReset ? (
          <>
            <MenuSeparator />
            <MenuItem onSelect={onReset}>Reset columns and widths</MenuItem>
          </>
        ) : null}
      </MenuContent>
    </MenuRoot>
  );
}
